/**
 * Shared time-bucket report modal (post-session + manual pencil).
 * Declared ROI = sessions × admin min/session.
 * This report stores user-validated minutes for calibration (does not rewrite declared ROI).
 */

import React, { useMemo, useState } from 'react';
import { X, Clock } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import './GarageRoiPromptModals.css';

export const ROI_TIME_BUCKETS = [
  { label: '< 5m', value: 3 },
  { label: '5-15m', value: 10 },
  { label: '15-30m', value: 22 },
  { label: '30-60m', value: 45 },
  { label: '> 1hr', value: 75 },
];

const GarageRoiReportModal = ({
  open,
  tool,
  source = 'manual',
  sessionId = null,
  onClose,
  onSaved,
}) => {
  const [selected, setSelected] = useState(null);
  const [customMinutes, setCustomMinutes] = useState('');
  const [sentiment, setSentiment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const minutes = useMemo(() => {
    if (customMinutes !== '') {
      const n = Number(customMinutes);
      return Number.isFinite(n) && n >= 0 ? n : null;
    }
    return selected;
  }, [customMinutes, selected]);

  const bucketLabel = useMemo(() => {
    if (customMinutes !== '') return 'custom';
    const b = ROI_TIME_BUCKETS.find((x) => x.value === selected);
    return b?.label || null;
  }, [customMinutes, selected]);

  if (!open || !tool) return null;

  const handleSave = async () => {
    if (minutes == null) {
      setError('Pick a time bucket or enter custom minutes');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await garageAPI.submitRoiReports([
        {
          toolId: tool.toolId || tool.id,
          minutesSaved: minutes,
          bucketLabel,
          sentiment,
          sessionId,
          source,
        },
      ]);
      onSaved?.({ minutes, bucketLabel, source });
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="roi-prompt-overlay" onClick={onClose}>
      <div className="roi-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="roi-prompt-header">
          <div>
            <div className="roi-prompt-kicker">
              <Clock size={14} /> How much time did this save you?
            </div>
            <h3>{tool.name || tool.title}</h3>
            <p>
              Team estimate:{' '}
              <strong>
                {tool.adminEstimate != null || tool.minPerSession != null
                  ? `${tool.adminEstimate ?? tool.minPerSession}m / session`
                  : 'not set'}
              </strong>
              . Your answer calibrates ROI — it does not change program totals today.
            </p>
          </div>
          <button type="button" className="roi-prompt-icon-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="roi-prompt-body">
          <div className="roi-bucket-row">
            {ROI_TIME_BUCKETS.map((b) => (
              <button
                key={b.label}
                type="button"
                className={`roi-bucket-btn ${selected === b.value && customMinutes === '' ? 'active' : ''}`}
                onClick={() => {
                  setSelected(b.value);
                  setCustomMinutes('');
                }}
              >
                {b.label}
              </button>
            ))}
          </div>

          <label className="roi-prompt-label">Or custom minutes</label>
          <input
            type="number"
            min={0}
            className="roi-prompt-input"
            placeholder="e.g. 12"
            value={customMinutes}
            onChange={(e) => {
              setCustomMinutes(e.target.value);
              setSelected(null);
            }}
          />

          <div className="roi-sentiment-row">
            <span className="roi-prompt-label">Was it useful? (optional)</span>
            {['helpful', 'neutral', 'not_helpful'].map((s) => (
              <button
                key={s}
                type="button"
                className={`roi-sentiment-btn ${sentiment === s ? 'active' : ''}`}
                onClick={() => setSentiment(sentiment === s ? null : s)}
              >
                {s === 'helpful' ? '👍' : s === 'neutral' ? '😐' : '👎'}
              </button>
            ))}
          </div>

          {error && <div className="roi-prompt-error">{error}</div>}
        </div>

        <div className="roi-prompt-footer">
          <button type="button" className="roi-prompt-secondary" onClick={onClose} disabled={saving}>
            Skip
          </button>
          <button type="button" className="roi-prompt-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GarageRoiReportModal;
